import type { Page } from '@chukua/common';
import { useEffect, useState } from 'react';
import { ApiError } from '../../../shared/shared.api';
import { toListParams, type ListQuerySchema } from '../../../shared/policies/list-params';
import { useListQuery, type BaseListQuery } from '../../../shared/hooks/use-list-query';

/** `queryKey` records which query the loaded page answers, so a newer query shows as refreshing over the old rows. */
export type ReportTableState<T> =
  { status: 'loading' } | { status: 'loaded'; page: Page<T>; queryKey: string } | { status: 'forbidden' } | { status: 'error'; error: unknown };

function toErrorState(error: unknown): ReportTableState<never> {
  return error instanceof ApiError && error.status === 403 ? { status: 'forbidden' } : { status: 'error', error };
}

/**
 * One page of a report's figures or records for the URL's list query (`useListQuery`), re-fetched when the query changes.
 * `load` must be stable (`useCallback` on the report key): a new one reloads.
 */
export function useReportTable<Q extends BaseListQuery, T>(schema: ListQuerySchema<Q>, load: (query: Q) => Promise<Page<T>>) {
  const list = useListQuery(schema);
  const { query } = list;
  const [state, setState] = useState<ReportTableState<T>>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    const queryKey = toListParams(query).toString();
    load(query)
      .then((page) => {
        if (!cancelled) setState({ status: 'loaded', page, queryKey });
      })
      .catch((error: unknown) => {
        if (!cancelled) setState(toErrorState(error));
      });
    return () => {
      cancelled = true;
    };
  }, [query, load]);

  const isRefreshing = state.status === 'loaded' && state.queryKey !== toListParams(query).toString();
  return { state, list, isRefreshing };
}
